// Server-driven rate limiter for vault upload `initialize` calls.
// Backend publishes X-RateLimit-Limit / -Remaining / -Reset on every response
// and Retry-After on 429s. We mirror that state locally to pace dispatch and
// retry on 429s. Headers are best-effort — proxies may strip them — so a
// fallback of FALLBACK_LIMIT/min keeps us safe.

const FALLBACK_INTERVAL_MS = 1000;
const MAX_RETRIES = 8;
const MAX_BACKOFF_MS = 30_000;

let knownLimit: number | null = null;
let remaining: number | null = null;
let resetAtMs: number | null = null;
let lastDispatchAt = 0;

let gate: Promise<void> = Promise.resolve();

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function runSerial<T>(work: () => Promise<T>): Promise<T> {
  const prev = gate;
  let release!: () => void;
  gate = new Promise<void>(resolve => {
    release = resolve;
  });
  await prev;
  try {
    return await work();
  } finally {
    release();
  }
}

interface ParsedHeaders {
  limit?: number;
  remaining?: number;
  resetAtMs?: number;
  retryAfterMs?: number;
}

function parseHeaders(headers: Headers | undefined): ParsedHeaders {
  const parsed: ParsedHeaders = {};
  if (!headers) return parsed;

  const limit = headers.get('X-RateLimit-Limit');
  if (limit !== null) {
    const n = Number(limit);
    if (Number.isFinite(n)) parsed.limit = n;
  }

  const rem = headers.get('X-RateLimit-Remaining');
  if (rem !== null) {
    const n = Number(rem);
    if (Number.isFinite(n)) parsed.remaining = n;
  }

  const reset = headers.get('X-RateLimit-Reset');
  if (reset !== null) {
    const n = Number(reset);
    if (Number.isFinite(n)) parsed.resetAtMs = n * 1000;
  }

  const retryAfter = headers.get('Retry-After');
  if (retryAfter !== null) {
    const seconds = Number(retryAfter);
    if (Number.isFinite(seconds)) {
      parsed.retryAfterMs = Math.max(0, seconds * 1000);
    } else {
      const dateMs = Date.parse(retryAfter);
      if (Number.isFinite(dateMs)) {
        parsed.retryAfterMs = Math.max(0, dateMs - Date.now());
      }
    }
  }

  return parsed;
}

function applyHeaders(parsed: ParsedHeaders): void {
  if (parsed.limit !== undefined) knownLimit = parsed.limit;

  if (parsed.resetAtMs !== undefined) {
    if (resetAtMs === null || parsed.resetAtMs > resetAtMs) {
      // Window rolled forward — replace state with the server's view.
      resetAtMs = parsed.resetAtMs;
      if (parsed.remaining !== undefined) remaining = parsed.remaining;
    } else if (parsed.resetAtMs === resetAtMs && parsed.remaining !== undefined) {
      // Same window — keep the more pessimistic (lower) remaining.
      remaining = remaining === null ? parsed.remaining : Math.min(remaining, parsed.remaining);
    }
    // else: stale response from a prior window; ignore.
  } else if (parsed.remaining !== undefined) {
    remaining = remaining === null ? parsed.remaining : Math.min(remaining, parsed.remaining);
  }
}

async function waitForSlot(): Promise<void> {
  if (remaining !== null && remaining > 0) return;

  if (remaining !== null && remaining <= 0 && resetAtMs !== null) {
    const waitMs = resetAtMs - Date.now();
    if (waitMs > 0) {
      // Jitter so callers waking up after the same reset don't thunder.
      await sleep(waitMs + Math.floor(Math.random() * 250));
    }
    // After waiting, at least one slot has rolled in. Server will reconcile.
    remaining = 1;
    resetAtMs = null;
    return;
  }

  if (remaining !== null && remaining <= 0) {
    // Exhausted with no known reset — fall back to per-second pacing.
    await sleep(FALLBACK_INTERVAL_MS);
    remaining = 1;
    return;
  }

  // No observations yet — pace at the fallback interval until headers seed state.
  if (knownLimit === null && lastDispatchAt > 0) {
    const sinceLast = Date.now() - lastDispatchAt;
    if (sinceLast < FALLBACK_INTERVAL_MS) {
      await sleep(FALLBACK_INTERVAL_MS - sinceLast);
    }
  }
}

export interface RateLimitedInitResult<T> {
  status: number;
  headers: Headers | undefined;
  value: T | undefined;
}

export async function executeRateLimitedInit<T>(
  fn: () => Promise<RateLimitedInitResult<T>>
): Promise<T> {
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    await runSerial(async () => {
      await waitForSlot();
      if (remaining !== null) remaining -= 1;
      lastDispatchAt = Date.now();
    });

    const result = await fn();
    applyHeaders(parseHeaders(result.headers));

    if (result.status === 429) {
      if (attempt >= MAX_RETRIES) {
        throw new Error('Too many uploads in a short time. Please wait a moment and try again.');
      }
      const parsed = parseHeaders(result.headers);
      let baseWaitMs: number;
      if (parsed.retryAfterMs !== undefined) {
        baseWaitMs = parsed.retryAfterMs;
      } else if (parsed.resetAtMs === undefined) {
        baseWaitMs = Math.min(2 ** attempt * 1000, MAX_BACKOFF_MS);
      } else {
        baseWaitMs = Math.max(0, parsed.resetAtMs - Date.now());
      }
      await sleep(baseWaitMs + Math.floor(Math.random() * 1000));
      continue;
    }

    if (result.value === undefined) {
      throw new Error('Upload initialization returned no data.');
    }
    return result.value;
  }
  throw new Error('Too many uploads in a short time. Please wait a moment and try again.');
}
